import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useFeatures } from '@/hooks/useFeatures';
import { bookingsService, type BookingDto, type CreateBookingRequest } from '@/services/bookings';
import {
  studentBillingService,
  type StudentSubscriptionCredit,
} from '@/services/studentBilling';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, Plus, X } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const StudentDashboardPage: React.FC = () => {
  const { enabledFeatures } = useFeatures();
  const { toast } = useToast();
  const [bookings, setBookings] = useState<BookingDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newBooking, setNewBooking] = useState<CreateBookingRequest>({
    title: '',
    description: '',
    startTime: new Date(),
    endTime: new Date(),
  });
  const [credits, setCredits] = useState<StudentSubscriptionCredit[]>([]);

  const bookingsEnabled = enabledFeatures.includes('bookings');
  const subscriptionsEnabled = enabledFeatures.includes('subscriptions');
  const hasBookableCredits = credits.some(
    (c) => c.status === 'active' && c.lessonCreditsBalance > 0
  );

  useEffect(() => {
    if (bookingsEnabled) {
      loadBookings();
      void studentBillingService
        .getLessonCredits()
        .then(setCredits)
        .catch(() => setCredits([]));
    }
  }, [bookingsEnabled]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      const data = await bookingsService.getBookings();
      setBookings(data);
    } catch (error) {
      console.error('Failed to load bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBooking = async () => {
    try {
      if (!newBooking.title || !newBooking.startTime || !newBooking.endTime) {
        toast({
          title: 'Error',
          description: 'Please fill in all required fields',
          variant: 'destructive',
        });
        return;
      }

      await bookingsService.createBooking(newBooking);
      await loadBookings();
      setShowCreateDialog(false);
      setNewBooking({
        title: '',
        description: '',
        startTime: new Date(),
        endTime: new Date(),
      });
      toast({
        title: 'Success',
        description: 'Booking created successfully',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to create booking',
        variant: 'destructive',
      });
    }
  };

  const handleCancelBooking = async (bookingId: string) => {
    try {
      await bookingsService.cancelBooking(bookingId);
      await loadBookings();
      toast({
        title: 'Success',
        description: 'Booking cancelled successfully',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to cancel booking',
        variant: 'destructive',
      });
    }
  };

  if (!bookingsEnabled) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="">
          <CardContent className="p-8 text-center text-gray-400">
            <Calendar className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg mb-2">Bookings feature is not available</p>
            <p className="text-sm">Please contact your teacher to enable this feature</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">Bookings</h1>
          <p className="text-gray-400">Manage your lesson bookings and schedule</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button disabled={!hasBookableCredits}>
              <Plus className="w-4 h-4 mr-2" />
              New Booking
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-foreground">Create New Booking</DialogTitle>
              <DialogDescription>Schedule a new booking session</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="title" className="text-foreground">Title *</Label>
                <Input
                  id="title"
                  value={newBooking.title}
                  onChange={(e) => setNewBooking({ ...newBooking, title: e.target.value })}
                  className="bg-slate-50 border-slate-200 text-foreground"
                />
              </div>
              <div>
                <Label htmlFor="description" className="text-foreground">Description</Label>
                <Textarea
                  id="description"
                  value={newBooking.description}
                  onChange={(e) => setNewBooking({ ...newBooking, description: e.target.value })}
                  className="bg-slate-50 border-slate-200 text-foreground"
                />
              </div>
              <div>
                <Label htmlFor="startTime" className="text-foreground">Start Time *</Label>
                <Input
                  id="startTime"
                  type="datetime-local"
                  value={new Date(newBooking.startTime).toISOString().slice(0, 16)}
                  onChange={(e) =>
                    setNewBooking({ ...newBooking, startTime: new Date(e.target.value) })
                  }
                  className="bg-slate-50 border-slate-200 text-foreground"
                />
              </div>
              <div>
                <Label htmlFor="endTime" className="text-foreground">End Time *</Label>
                <Input
                  id="endTime"
                  type="datetime-local"
                  value={new Date(newBooking.endTime).toISOString().slice(0, 16)}
                  onChange={(e) =>
                    setNewBooking({ ...newBooking, endTime: new Date(e.target.value) })
                  }
                  className="bg-slate-50 border-slate-200 text-foreground"
                />
              </div>
              <Button onClick={handleCreateBooking} className="w-full">
                Create Booking
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {!hasBookableCredits && (
        <Card className="mb-4 border-amber-200 bg-amber-50">
          <CardContent className="p-4 text-sm text-amber-950">
            <p className="font-medium mb-1">Subscription required to book lessons</p>
            <p className="text-amber-900/90">
              You need an active subscription with lesson credits.{' '}
              {subscriptionsEnabled ? (
                <Link to="/billing/subscriptions" className="text-violet-700 font-medium underline">
                  Subscribe or view credits
                </Link>
              ) : (
                'Ask your school to enable the Subscriptions feature, then subscribe under Billing.'
              )}
            </p>
          </CardContent>
        </Card>
      )}

      <Card className="">
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            My Bookings
          </CardTitle>
          <CardDescription>View and manage your bookings</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading...</div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Calendar className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>No bookings yet. Create your first booking!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {bookings.map((booking) => (
                <div
                  key={booking.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200"
                >
                  <div className="flex-1">
                    <p className="text-foreground font-medium">{booking.title}</p>
                    <p className="text-sm text-gray-400">
                      {new Date(booking.startTime).toLocaleString()} -{' '}
                      {new Date(booking.endTime).toLocaleString()}
                    </p>
                    {booking.description && (
                      <p className="text-sm text-gray-500 mt-1">{booking.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 sm:gap-4">
                    <span
                      className={`text-xs px-2 py-1 rounded ${
                        booking.status === 'confirmed'
                          ? 'bg-green-500/20 text-green-400'
                          : booking.status === 'pending'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-gray-500/20 text-gray-400'
                      }`}
                    >
                      {booking.status}
                    </span>
                    {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleCancelBooking(booking.id)}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentDashboardPage;

